import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from './store/store';
import { logout } from './store/authSlice';
import { isTokenExpired } from './utils/jwt';
import DashboardLayout from './components/layout/DashboardLayout';
import LoginPage from './pages/Auth/LoginPage';
import RegisterPage from './pages/Auth/RegisterPage';
import VerifyOTPPage from './pages/Auth/VerifyOTPPage';
import ForgotPasswordPage from './pages/Auth/ForgotPasswordPage';
import LocationPage from './pages/Auth/Location';
import DashboardPage from './pages/DashboardPage';
import OrderDetailPage from './pages/OrderDetailPage';
import AnalyticsPage from './pages/AnalyticsPage';
import ProfilePage from './pages/ProfilePage';
import WalletPage from './Wallet/WalletPage';
import ViewAllActivity from './Wallet/ViewAllActivity';
import ReturnOrdersPage from './pages/Returns/ReturnOrdersPage';
import ProductsPage from './pages/Products/ProductsPage';
import AddProductPage from './pages/Products/AddProductPage';
import EditProductPage from './pages/Products/EditProductPage';
import InventoryPage from './pages/Inventory/InventoryPage';
import OrdersPage from './pages/Orders/OrdersPage';
import PickupPage from './pages/Pickup/PickupPage';
import TicketsPage from './pages/Tickets/TicketsPage';
import CreateTicket from './pages/Tickets/CreateTicket';
import TicketDetails from './pages/Tickets/TicketDetails';
import TicketCategory from './pages/Tickets/TicketCategory';
import IdleTimeoutHandler from './components/IdleTimeoutHandler';

const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, token } = useSelector((s: RootState) => s.auth);
  const dispatch = useDispatch();

  if (!isAuthenticated || isTokenExpired(token)) {
    if (token && isTokenExpired(token)) {
      dispatch(logout());
    }
    return <Navigate to="/login" replace />;
  }
  return children;
};

const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useSelector((s: RootState) => s.auth);
  return !isAuthenticated ? children : <Navigate to="/dashboard" replace />;
};

export default function App() {
  const { isAuthenticated, token } = useSelector((s: RootState) => s.auth);
  const dispatch = useDispatch();

  useEffect(() => {
    let interval: any;
    if (isAuthenticated && token) {
      interval = setInterval(() => {
        if (isTokenExpired(token)) {
          dispatch(logout());
        }
      }, 30000); // Check every 30 seconds
    }
    return () => clearInterval(interval);
  }, [isAuthenticated, token, dispatch]);

  return (
    <BrowserRouter>
      <IdleTimeoutHandler />
      <Routes>
        {/* Public */}
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
        <Route path="/verify-otp" element={<PublicRoute><VerifyOTPPage /></PublicRoute>} />
        <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
        <Route path="/location" element={<PublicRoute><LocationPage /></PublicRoute>} />

        {/* Protected */}
        <Route path="/" element={<PrivateRoute><DashboardLayout /></PrivateRoute>}>
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />

          {/* products */}
          <Route path="products" element={<ProductsPage />} />
          <Route path="products/add" element={<AddProductPage />} />
          <Route path="products/edit/:id" element={<EditProductPage />} />

          {/* orders */}
          <Route path="orders" element={<OrdersPage />} />
          <Route path="orders/:id" element={<OrderDetailPage />} />
          <Route path="returns" element={<ReturnOrdersPage />} />
          <Route path="pickup" element={<PickupPage />} />
          <Route path="inventory" element={<InventoryPage />} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="wallet" element={<WalletPage />} />
          <Route path="wallet/activity" element={<ViewAllActivity />} />

          {/* Product Requests / Tickets */}
          <Route path="tickets" element={<TicketsPage />} />
          <Route path="tickets/category" element={<TicketCategory />} />
          <Route path="tickets/create" element={<CreateTicket />} />
          <Route path="tickets/:id" element={<TicketDetails />} />
        </Route>

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
